/**
 * Trend across the snapshot frames the range slicer has selected.
 *
 * Stacked AreaChart when the API marks the series stacked (the category bases),
 * grouped LineChart otherwise (approved vs filled seats). The selected period is
 * marked with a ReferenceLine plus a ReferenceDot per series, so the tiles above
 * and the chart below can never be read as describing different dates.
 *
 * One control, not two. The RANGE (set by the slicer on the page) decides which
 * frames are drawn. There is deliberately no in-chart brush: with eight frames
 * it duplicated the slicer and clipped its own labels at both edges. The readout
 * under the chart always states what is actually drawn, so a narrowed chart
 * never reads as the whole history.
 *
 * Nulls are gaps, never zeroes: `connectNulls={false}` on every mark, and the
 * as-at marker dots skip a series with no value at that frame rather than
 * plotting it at 0 (a withheld or unpublished point is not a headcount of nil).
 *
 * The legend is rendered by this component, not by recharts, because every
 * series has to carry its own definition tooltip.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ReferenceLine,
  ReferenceDot,
} from 'recharts';
import { LegendSwatch, PanelShell, PanelSkeleton, PanelUnavailable } from './PanelShell';
import {
  ACCENT,
  AXIS,
  GRID,
  INK,
  SERIES_FALLBACK_COLORS,
  categoryLabel,
  fmtDay,
  fmtNum,
  fmtPct,
  type DefLookup,
  type WorkforceBasis,
  type WorkforceSummary,
  type WorkforceTrend,
} from './apiContract';

const DECIMAL_1DP = new Intl.NumberFormat('en-GB', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

/**
 * Shared value formatter for every time-series tooltip on this page (the
 * payroll panel imports it too, so the two charts cannot drift apart).
 *
 * A missing value is "withheld", never 0 and never blank: on this page null
 * means either suppressed under the min-n rule or not published for that frame,
 * and both are statements about disclosure, not about headcount.
 */
export function formatTimeSeriesValue(value: unknown, opts?: { decimals?: boolean }): string {
  if (value === null || value === undefined || value === '') return 'withheld';
  const n = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(n)) return String(value);
  // Not fmtOne: that is toFixed(1) with no grouping, which reads "1669.2" for a
  // four-figure FTE. Series values here run into the thousands and need both.
  return opts?.decimals ? DECIMAL_1DP.format(n) : fmtNum(n);
}

interface TrendPanelProps {
  /** Already filtered to the selected range by the page. */
  trend: WorkforceTrend | null;
  summary: WorkforceSummary | null;
  basis: WorkforceBasis;
  asAt: string | null;
  periodLabel: string;
  /** Frames published in total, so the panel can say "5 of 8" honestly. */
  framesTotal?: number;
  loading: boolean;
  def: DefLookup;
}

type ChartRow = Record<string, string | number | null>;

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

const numberOrNull = (v: string | number | null | undefined): number | null =>
  typeof v === 'number' && !Number.isNaN(v) ? v : null;

export const TrendPanel: React.FC<TrendPanelProps> = ({
  trend,
  summary,
  basis,
  asAt,
  periodLabel,
  framesTotal,
  loading,
  def,
}) => {
  const series = trend?.series ?? [];
  const stacked = series.some((s) => s.stacked);

  const rows = useMemo<ChartRow[]>(() => {
    const byDate = new Map<string, ChartRow>();
    series.forEach((s) => {
      s.points?.forEach((p) => {
        const row = byDate.get(p.as_at) ?? { as_at: p.as_at, label: p.label };
        row[s.key] = p.value;
        byDate.set(p.as_at, row);
      });
    });
    return Array.from(byDate.values()).sort((a, b) => String(a.as_at).localeCompare(String(b.as_at)));
  }, [series]);

  const labelByDate = useMemo(() => {
    const m = new Map<string, string>();
    rows.forEach((r) => m.set(String(r.as_at), String(r.label ?? r.as_at)));
    return m;
  }, [rows]);

  // ---- in-chart zoom -------------------------------------------------------
  // Held as indices into `rows`. The range slicer changes which frames exist,
  // so the zoom resets whenever that window moves — a stale index pair against
  // a shorter series would silently crop the chart.
  const [zoom, setZoom] = useState<{ start: number; end: number } | null>(null);
  const rowsKey = rows.length ? `${rows[0].as_at}|${rows[rows.length - 1].as_at}` : '';
  useEffect(() => {
    setZoom(null);
  }, [rowsKey]);

  const lastIndex = Math.max(0, rows.length - 1);
  const vStart = zoom ? clamp(zoom.start, 0, lastIndex) : 0;
  const vEnd = zoom ? clamp(zoom.end, vStart, lastIndex) : lastIndex;
  const viewRows = rows.slice(vStart, vEnd + 1);

  // Marker dots sit at the drawn height: cumulative for a stack, raw otherwise.
  // A null breaks the stack — every height above it is unknown, so the run
  // stops rather than resuming at a height that would be quietly wrong.
  const markers = useMemo(() => {
    if (!asAt) return [] as { key: string; y: number; color: string }[];
    const row = rows.find((r) => r.as_at === asAt);
    if (!row) return [];
    const out: { key: string; y: number; color: string }[] = [];
    let cumulative = 0;
    for (let i = 0; i < series.length; i += 1) {
      const s = series[i];
      const value = numberOrNull(row[s.key]);
      if (value === null) {
        if (stacked) break;
        continue;
      }
      cumulative += value;
      out.push({
        key: s.key,
        y: stacked ? cumulative : value,
        color: s.color || SERIES_FALLBACK_COLORS[i % SERIES_FALLBACK_COLORS.length],
      });
    }
    return out;
  }, [rows, series, asAt, stacked]);

  // Value label on the last point actually drawn. On a stack that means the
  // stack total — and only when every component is present, because a total
  // built over a withheld component would understate itself without saying so.
  const endpoint = useMemo(() => {
    const row = viewRows[viewRows.length - 1];
    if (!row) return null;
    const x = String(row.as_at);
    if (stacked) {
      let total = 0;
      for (let i = 0; i < series.length; i += 1) {
        const value = numberOrNull(row[series[i].key]);
        if (value === null) return null;
        total += value;
      }
      return { x, items: [{ key: '__stack__', y: total, value: total, color: INK }] };
    }
    const items = series
      .map((s, i) => ({
        key: s.key,
        value: numberOrNull(row[s.key]),
        color: s.color || SERIES_FALLBACK_COLORS[i % SERIES_FALLBACK_COLORS.length],
      }))
      .filter((it): it is { key: string; value: number; color: string } => it.value !== null)
      .map((it) => ({ key: it.key, y: it.value, value: it.value, color: it.color }));
    return items.length ? { x, items } : null;
  }, [viewRows, series, stacked]);

  // Stacked charts read top-down, so the legend reverses — but each entry keeps
  // its ORIGINAL index so the colour fallback stays aligned with the chart.
  const legend = useMemo(() => {
    const entries = series.map((s, i) => ({ s, i }));
    return stacked ? entries.reverse() : entries;
  }, [series, stacked]);

  const readout = useMemo(() => {
    if (!summary) return null;
    const p = summary.people ?? {};
    if (basis === 'positions') {
      const pos = summary.positions;
      const rate = pos?.fill_rate ?? (pos?.approved ? (pos.filled / pos.approved) * 100 : null);
      return (
        <>
          At <b className="text-heritage-blue">{periodLabel}</b>: <b>{fmtNum(pos?.approved)}</b> approved
          {' · '}
          <b>{fmtNum(pos?.filled)}</b> filled {' · '} <b>{fmtNum(pos?.vacant)}</b> vacant
          {rate !== null && rate !== undefined ? <> {' · '} {fmtPct(rate)} fill rate</> : null}
        </>
      );
    }
    if (basis === 'employees') {
      return (
        <>
          At <b className="text-heritage-blue">{periodLabel}</b>: <b>{fmtNum(summary.employees)}</b>{' '}
          employees — {fmtNum(p.Permanent)} permanent, {fmtNum(p.FTC)} fixed-term
        </>
      );
    }
    const parts = Object.entries(p)
      .filter(([, v]) => v > 0)
      .map(([k, v]) => `${fmtNum(v)} ${categoryLabel(k)}`);
    return (
      <>
        At <b className="text-heritage-blue">{periodLabel}</b>: <b>{fmtNum(summary.engaged)}</b> engaged
        people — {parts.join(', ')}
      </>
    );
  }, [summary, basis, periodLabel]);

  const total = framesTotal ?? rows.length;
  // The zoom can push the selected date off the drawn window. Say so — a
  // marker that is simply absent reads as "there is no selected date".
  const asAtOutsideZoom = !!asAt && !viewRows.some((r) => r.as_at === asAt);
  const drawnFrom = viewRows.length ? labelByDate.get(String(viewRows[0].as_at)) : null;
  const drawnTo = viewRows.length
    ? labelByDate.get(String(viewRows[viewRows.length - 1].as_at))
    : null;

  const axes = (
    <>
      <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
      <XAxis
        dataKey="as_at"
        tick={{ fontSize: 11 }}
        tickLine={false}
        axisLine={{ stroke: AXIS }}
        tickFormatter={(v: string) => labelByDate.get(v) ?? v}
      />
      <YAxis
        tick={{ fontSize: 11 }}
        tickLine={false}
        axisLine={false}
        width={48}
        tickFormatter={(v: number) => fmtNum(v)}
      />
      <RechartsTooltip
        labelStyle={{ fontWeight: 600 }}
        contentStyle={{ fontSize: 12 }}
        labelFormatter={(v: string) => fmtDay(v)}
        formatter={(value: number | string, name: string) => [formatTimeSeriesValue(value), name]}
      />
    </>
  );

  const overlays = (
    <>
      {asAt ? (
        <ReferenceLine
          x={asAt}
          stroke={INK}
          strokeDasharray="3 4"
          strokeOpacity={0.6}
          label={{ value: 'as at', position: 'top', fontSize: 9, fill: '#94a3b8' }}
        />
      ) : null}
      {asAt
        ? markers.map((m) => (
            <ReferenceDot
              key={m.key}
              x={asAt}
              y={m.y}
              r={4.5}
              fill={m.color}
              stroke="#ffffff"
              strokeWidth={2}
              isFront
            />
          ))
        : null}
      {endpoint
        ? endpoint.items.map((it) => (
            <ReferenceDot
              key={`end-${it.key}`}
              x={endpoint.x}
              y={it.y}
              r={0}
              fill="none"
              stroke="none"
              isFront
              label={{
                value: fmtNum(it.value),
                position: 'top',
                fontSize: 11,
                fontWeight: 600,
                fill: it.color,
              }}
            />
          ))
        : null}
      {/* No brush here. The range slicer above this chart already selects
          frames, so a brush would be a second control for the same job — and
          with eight frames its traveller labels ("Aug 2024", "31 Jul 2026",
          "Today") clipped at both chart edges. Payroll keeps its brush: 55
          monthly points genuinely need one. */}
    </>
  );

  const chart = stacked ? (
    <AreaChart data={rows} margin={{ top: 16, right: 44, left: 8, bottom: 0 }}>
      {axes}
      {series.map((s, i) => {
        const color = s.color || SERIES_FALLBACK_COLORS[i % SERIES_FALLBACK_COLORS.length];
        return (
          <Area
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.label}
            stackId="workforce"
            stroke={color}
            strokeWidth={1.25}
            fill={color}
            fillOpacity={0.85}
            connectNulls={false}
            dot={{ r: 2.5, fill: color, stroke: '#ffffff', strokeWidth: 1 }}
            activeDot={{ r: 5, stroke: '#ffffff', strokeWidth: 2 }}
            isAnimationActive={false}
          />
        );
      })}
      {overlays}
    </AreaChart>
  ) : (
    <LineChart data={rows} margin={{ top: 16, right: 44, left: 8, bottom: 0 }}>
      {axes}
      {series.map((s, i) => {
        const color = s.color || SERIES_FALLBACK_COLORS[i % SERIES_FALLBACK_COLORS.length];
        return (
          <Line
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.label}
            stroke={color}
            strokeWidth={2.5}
            connectNulls={false}
            dot={{ r: 2.75, fill: color, stroke: '#ffffff', strokeWidth: 1 }}
            activeDot={{ r: 5, stroke: '#ffffff', strokeWidth: 2 }}
            isAnimationActive={false}
          />
        );
      })}
      {overlays}
    </LineChart>
  );

  return (
    <PanelShell
      title={trend?.title || 'Workforce trend'}
      defKey="trend"
      def={def}
      window={
        'Point-in-time reconstruction at each snapshot date, on the budget-aligned rule. Follows the basis toggle. ' +
        'The range slicer above chooses which frames are drawn; the dashed marker is the single “as at” date the tiles are computed at.'
      }
      right={
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {legend.map(({ s, i }) => (
            <LegendSwatch
              key={s.key}
              color={s.color || SERIES_FALLBACK_COLORS[i % SERIES_FALLBACK_COLORS.length]}
              label={s.label}
              defKey={s.key}
              def={def}
            />
          ))}
        </div>
      }
      footnote={
        <>
          {trend?.note ? <span>{trend.note} </span> : null}
          A frame with no published value is drawn as a gap, not a zero — the line breaks rather than
          dipping, and the tooltip says “withheld”. Drag the handles under the chart to zoom inside the
          selected range.
        </>
      }
    >
      {loading && !trend ? (
        <PanelSkeleton height={300} />
      ) : !trend || !rows.length ? (
        <PanelUnavailable what="The trend" />
      ) : (
        <>
          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>{chart}</ResponsiveContainer>
          </div>
          <div className="mt-2 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
            {readout ? (
              <span className="text-[12.5px] tabular-nums text-gray-600">{readout}</span>
            ) : (
              <span />
            )}
            <span className="text-[11px] tabular-nums text-gray-500">
              Drawn: {drawnFrom ?? '—'} – {drawnTo ?? '—'} · {viewRows.length} of {total} frame
              {total === 1 ? '' : 's'}
              {asAtOutsideZoom ? (
                <span className="text-amber-700"> · “as at” {periodLabel} is outside this zoom</span>
              ) : null}
            </span>
          </div>
        </>
      )}
    </PanelShell>
  );
};
